import React from "react";
import { Link } from "react-router-dom";
import { Tooltip, Chip } from "@mui/material";
import type { GridColDef, GridColumnVisibilityModel, GridSortModel } from "@mui/x-data-grid";
import { DateTime } from "luxon";
import { formatDateAsLocaleDateString } from "utils/formatters/DateFormatters";
import { formatFullName } from "utils/formatters/NameFormatters";
import type { Subscription } from "client/api-v2";
import { DataTable } from "components/DataTable";
import { EmptyValue, MutedPlaceholder } from "components/MutedPlaceholder";
import { subscriptionRoutes } from "areas/subscriptions/subscription-routes";
import { subscriptionStatusColor } from "../constants/subscription-status-color";

interface SubscriptionWithSerial {
  id: string;
  serial: string;
  dnsPrefix?: string | null;
  subscription: Subscription;
}

interface SubscriptionListProps {
  subscriptions: SubscriptionWithSerial[];
  totalCount: number;
  loading?: boolean;
  page: number;
  pageSize?: number;
  sortModel: GridSortModel;
  columnVisibilityModel?: GridColumnVisibilityModel;
  onPageChange: (page: number) => void;
  onPageSizeChange?: (pageSize: number) => void;
  onSortModelChange?: (mode: GridSortModel) => void;
  onColumnVisibilityModelChange?: (visModel: GridColumnVisibilityModel) => void;
}

const columns: GridColDef<SubscriptionWithSerial>[] = [
  {
    field: "serial",
    headerName: "License Serial",
    minWidth: 180,
    flex: 1,
    renderCell: (params) => {
      const serial = params.row.serial;
      if (!serial) return "";

      return (
        <Link to={subscriptionRoutes.detail(params.row.id || "").root} title="View subscription details">
          {serial}
        </Link>
      );
    },
  },
  {
    field: "dnsPrefix",
    headerName: "DNS",
    minWidth: 150,
    flex: 1,
    renderCell: (params) => {
      if (!params.row.dnsPrefix) {
        return <EmptyValue />;
      }
      return params.row.dnsPrefix;
    },
  },
  {
    field: "friendlyName",
    headerName: "Friendly Name",
    minWidth: 180,
    flex: 1,
    valueGetter: (_, row) => {
      return row.subscription.friendlyName || "";
    },
    renderCell: (params) => {
      if (!params.row.subscription.friendlyName) {
        return <MutedPlaceholder>None set</MutedPlaceholder>;
      }
      return params.row.subscription.friendlyName;
    },
  },
  {
    field: "ownerEmail",
    headerName: "Owner",
    minWidth: 250,
    flex: 1,
    renderCell: (params) => {
      const user = params.row.subscription.ownerUser;
      if (!user) return "";

      const name = formatFullName(user);
      const email = user.email || "";

      if (name && email) {
        return (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              height: "100%",
              lineHeight: "1.3",
            }}
          >
            <span style={{ fontWeight: 500 }}>{name}</span>
            <span style={{ fontSize: "0.85em", opacity: 0.7 }}>{email}</span>
          </div>
        );
      }

      return name || email;
    },
  },
  {
    field: "planName",
    headerName: "Plan",
    minWidth: 150,
    flex: 1,
    valueGetter: (_, row) => {
      return row.subscription.plan.displayName;
    },
  },
  {
    field: "status",
    headerName: "Status",
    minWidth: 120,
    renderCell: (params) => {
      const status = params.row.subscription.status || "Unknown";
      return <Chip label={status} color={subscriptionStatusColor(status)} size="small" variant="outlined" />;
    },
  },
  {
    field: "termStartDate",
    headerName: "Term Start",
    minWidth: 160,
    flex: 1,
    valueGetter: (_, row) => {
      return row.subscription.termStartDate;
    },
    renderCell: (params) => {
      if (!params.value) return "";
      const date = DateTime.fromISO(params.value, { zone: "utc" });
      return (
        <Tooltip title={formatDateAsLocaleDateString(date)}>
          <span>{formatDateAsLocaleDateString(date)}</span>
        </Tooltip>
      );
    },
  },
  {
    field: "termEndDate",
    headerName: "Term End",
    minWidth: 160,
    flex: 1,
    valueGetter: (_, row) => {
      return row.subscription.termEndDate;
    },
    renderCell: (params) => {
      if (!params.value) return "∞";
      const date = DateTime.fromISO(params.value, { zone: "utc" });
      return (
        <Tooltip title={formatDateAsLocaleDateString(date)}>
          <span>{formatDateAsLocaleDateString(date)}</span>
        </Tooltip>
      );
    },
  },
];

export function SubscriptionList({
  subscriptions,
  totalCount,
  loading,
  page,
  pageSize,
  sortModel,
  columnVisibilityModel,
  onPageChange,
  onPageSizeChange,
  onSortModelChange,
  onColumnVisibilityModelChange,
}: SubscriptionListProps) {
  return (
    <DataTable
      rows={subscriptions}
      columns={columns}
      rowCount={totalCount}
      loading={loading}
      page={page}
      pageSize={pageSize}
      onPageChange={onPageChange}
      onPageSizeChange={onPageSizeChange}
      pagingMode="server"
      sortingMode="server"
      sortModel={sortModel}
      onSortModelChange={onSortModelChange}
      columnVisibilityModel={columnVisibilityModel}
      onColumnVisibilityModelChange={onColumnVisibilityModelChange}
    />
  );
}
